import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Neumorphic Design System
const nm = {
  bg: '#e8ecf1',
  shadow: '8px 8px 16px #c5c9cd, -8px -8px 16px #ffffff',
  shadowSm: '4px 4px 8px #c5c9cd, -4px -4px 8px #ffffff',
  shadowInset: 'inset 4px 4px 8px #c5c9cd, inset -4px -4px 8px #ffffff',
  shadowLg: '12px 12px 24px #c5c9cd, -12px -12px 24px #ffffff',
};

// Provider logo mapping
const providerLogos = {
  'Anthropic': '/claude-ai-icon.svg',
  'anthropic': '/claude-ai-icon.svg',
  'OpenAI': '/images/logos/openai.svg',
  'openai': '/images/logos/openai.svg',
  'Google': '/google-icon.svg',
  'google': '/google-icon.svg',
  'Meta': '/meta-icon.svg',
  'meta-llama': '/meta-icon.svg',
  'xAI': '/xai-icon.svg',
  'x-ai': '/xai-icon.svg',
  'Mistral': '/mistral-icon.svg',
  'mistralai': '/mistral-icon.svg',
  'Cohere': '/images/logos/cohere.svg',
  'cohere': '/images/logos/cohere.svg',
  'DeepSeek': '/deepseek-logo-icon.svg',
  'deepseek': '/deepseek-logo-icon.svg',
  'Qwen': '/qwen-ai-icon.svg',
  'qwen': '/qwen-ai-icon.svg',
  'Microsoft': '/microsoft-icon.svg',
  'microsoft': '/microsoft-icon.svg',
  'Perplexity': '/images/logos/perplexity.svg',
  'perplexity': '/images/logos/perplexity.svg',
  'AI21': '/ai21-icon.svg',
  'ai21': '/ai21-icon.svg',
  'ByteDance': '/bytedance.svg',
  'Runway': '/runway.svg',
  'Kling': '/kling-icon.svg',
  'Ideogram': '/ideogram.svg',
  'Suno': '/suno.svg',
  'Kei.ai': '/kei-ai-icon.svg',
};

const API_URL = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';

export function ModelsViewNew({ models, onToggleStatus, loading, onReload }) {
  const [allModels, setAllModels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterFunction, setFilterFunction] = useState('all');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAllModels();
  }, []);

  const loadAllModels = async () => {
    setIsLoading(true);
    
    try {
      // Fetch models from database via admin API
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/models`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const dbModels = data.models || [];

        console.log(`Fetched ${dbModels.length} models from database`);
                // Transform database models to display format
        const models = dbModels.map((model, index) => {
          // Determine function type from category or functions array
          let functionType = 'Chat';
          if (model.category === 'video') {
            functionType = 'Video';
          } else if (model.category === 'music') {
            functionType = 'Music';
          } else if (model.category === 'image') {
            functionType = 'Image';
          } else if (model.category === 'chat') {
            functionType = 'Chat';
          } else if (model.functions && model.functions.includes('video')) {
            functionType = 'Video';
          } else if (model.functions && model.functions.includes('music')) {
            functionType = 'Music';
          } else if (model.functions && model.functions.includes('image')) {
            functionType = 'Image';
          }

          // Calculate pricing
          const promptPerMillion = (model.pricing_input || 0) * 1000000;
          const completionPerMillion = (model.pricing_output || 0) * 1000000;
          const totalPerMillion = promptPerMillion + completionPerMillion;

          // Determine if this model is set as default for its function
          let isDefault = false;
          if (functionType === 'Chat') isDefault = model.is_default_chat || false;
          else if (functionType === 'Image') isDefault = model.is_default_image || false;
          else if (functionType === 'Video') isDefault = model.is_default_video || false;
          else if (functionType === 'Music') isDefault = model.is_default_music || false;

          return {
            id: model.id,
            srNo: index + 1,
            function: functionType,
            provider: model.provider || 'Unknown',
            modelName: model.name || model.model_id,
            contextWindow: model.context_window ? model.context_window.toLocaleString() : 'N/A',
            inputPerMillion: functionType === 'Chat' ? `$${promptPerMillion.toFixed(2)}` : 'TBD',
            outputPerMillion: functionType === 'Chat' ? `$${completionPerMillion.toFixed(2)}` : 'N/A',
            total: functionType === 'Chat' ? `$${totalPerMillion.toFixed(2)}` : 'TBD',
            status: model.status || 'active',
            isDefault: isDefault,
            source: 'database'
          };
        });

        // Sort models
        models.sort((a, b) => {
          if (a.function !== b.function) {
            const functionOrder = { 'Chat': 1, 'Image': 2, 'Video': 3, 'Music': 4 };
            return functionOrder[a.function] - functionOrder[b.function];
          }
          if (a.provider !== b.provider) {
            return a.provider.localeCompare(b.provider);
          }
          return a.modelName.localeCompare(b.modelName);
        });

        // Reassign SR numbers
        models.forEach((model, index) => {
          model.srNo = index + 1;
        });

        setAllModels(models);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncModels = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/sync-all-models`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Sync result:', data);
        alert(`Synced ${data.synced} models (${data.created} created, ${data.updated} updated)`);
        // Reload models after sync
        await loadAllModels();
      } else {
        alert('Failed to sync models');
      }
    } catch (error) {
      console.error('Error syncing models:', error);
      alert('Error syncing models: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (model) => {
    try {
      const newStatus = model.status === 'active' ? 'inactive' : 'active';
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_URL}/api/admin/models/${model.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        // Update local state
        const updatedModels = allModels.map(m => 
          m.id === model.id ? { ...m, status: newStatus } : m
        );
        setAllModels(updatedModels);
      } else {
        alert('Failed to update model status');
      }
    } catch (error) {
      console.error('Error toggling model status:', error);
      alert('Error updating status: ' + error.message);
    }
  };

  const handleToggleDefault = async (model) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Determine which default field to update based on function type
      let defaultField = '';
      if (model.function === 'Chat') defaultField = 'is_default_chat';
      else if (model.function === 'Image') defaultField = 'is_default_image';
      else if (model.function === 'Video') defaultField = 'is_default_video';
      else if (model.function === 'Music') defaultField = 'is_default_music';
      
      const newDefaultValue = !model.isDefault;
      
      const response = await fetch(`${API_URL}/api/admin/models/${model.id}/set-default`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          function: model.function.toLowerCase(),
          isDefault: newDefaultValue 
        })
      });

      if (response.ok) {
        // Update local state - unset other defaults of same function type
        const updatedModels = allModels.map(m => {
          if (m.function === model.function && m.id !== model.id) {
            return { ...m, isDefault: false };
          } else if (m.id === model.id) {
            return { ...m, isDefault: newDefaultValue };
          }
          return m;
        });
        setAllModels(updatedModels);
        alert(`${model.modelName} ${newDefaultValue ? 'set as' : 'removed from'} default ${model.function} model`);
      } else {
        alert('Failed to update default model');
      }
    } catch (error) {
      console.error('Error toggling default model:', error);
      alert('Error updating default: ' + error.message);
    }
  };

  const filteredModels = allModels.filter(model => {
    const matchesSearch = searchQuery === '' || 
      model.modelName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      model.provider.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFunction = filterFunction === 'all' || model.function === filterFunction;
    const matchesProvider = filterProvider === 'all' || model.provider === filterProvider;
    const matchesStatus = filterStatus === 'all' || model.status === filterStatus;
    
    return matchesSearch && matchesFunction && matchesProvider && matchesStatus;
  });

  const uniqueProviders = [...new Set(allModels.map(m => m.provider))].sort();
  const chatCount = allModels.filter(m => m.function === 'Chat').length;
  const imageCount = allModels.filter(m => m.function === 'Image').length;
  const videoCount = allModels.filter(m => m.function === 'Video').length;
  const musicCount = allModels.filter(m => m.function === 'Music').length;

  return (
    <div>
      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '24px' }}>
        <div style={{ padding: '20px', background: nm.bg, borderRadius: '16px', boxShadow: nm.shadowSm }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg className="w-5 h-5" fill="#fff" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Chat Models</div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{chatCount}</div>
            </div>
          </div>
        </div>

        <div style={{ padding: '20px', background: nm.bg, borderRadius: '16px', boxShadow: nm.shadowSm }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg className="w-5 h-5" fill="#fff" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Image Models</div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{imageCount}</div>
            </div>
          </div>
        </div>

        <div style={{ padding: '20px', background: nm.bg, borderRadius: '16px', boxShadow: nm.shadowSm }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg className="w-5 h-5" fill="#fff" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Video Models</div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{videoCount}</div>
            </div>
          </div>
        </div>

        <div style={{ padding: '20px', background: nm.bg, borderRadius: '16px', boxShadow: nm.shadowSm }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg className="w-5 h-5" fill="#fff" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Music Models</div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{musicCount}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div style={{ padding: '24px', background: nm.bg, borderRadius: '16px', boxShadow: nm.shadowSm, marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ flex: 1, minWidth: '250px' }}>
            <div style={{ position: 'relative' }}>
              <svg style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px' }} fill="#94a3b8" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/></svg>
              <input
                type="text"
                placeholder="Search models..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', paddingLeft: '40px', paddingRight: '16px', paddingTop: '10px', paddingBottom: '10px', background: nm.bg, border: 'none', borderRadius: '12px', fontSize: '14px', color: '#0f172a', boxShadow: nm.shadowInset, outline: 'none' }}
              />
            </div>
          </div>

          {/* Function Filter */}
          <select
            value={filterFunction}
            onChange={(e) => setFilterFunction(e.target.value)}
            style={{ padding: '10px 16px', background: nm.bg, border: 'none', borderRadius: '12px', fontSize: '14px', color: '#0f172a', fontWeight: '600', boxShadow: nm.shadowSm, cursor: 'pointer', outline: 'none' }}
          >
            <option value="all">All Functions</option>
            <option value="Chat">Chat ({chatCount})</option>
            <option value="Image">Image ({imageCount})</option>
            <option value="Video">Video ({videoCount})</option>
            <option value="Music">Music ({musicCount})</option>
          </select>

          {/* Provider Filter */}
          <select
            value={filterProvider}
            onChange={(e) => setFilterProvider(e.target.value)}
            style={{ padding: '10px 16px', background: nm.bg, border: 'none', borderRadius: '12px', fontSize: '14px', color: '#0f172a', fontWeight: '600', boxShadow: nm.shadowSm, cursor: 'pointer', outline: 'none' }}
          >
            <option value="all">All Providers</option>
            {uniqueProviders.map(provider => (
              <option key={provider} value={provider}>{provider}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ padding: '10px 16px', background: nm.bg, border: 'none', borderRadius: '12px', fontSize: '14px', color: '#0f172a', fontWeight: '600', boxShadow: nm.shadowSm, cursor: 'pointer', outline: 'none' }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Sync Models Button */}
          <button
            onClick={handleSyncModels}
            disabled={isLoading}
            style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: isLoading ? 'not-allowed' : 'pointer', boxShadow: '4px 4px 12px rgba(16,185,129,0.25)', transition: 'all 0.2s ease', opacity: isLoading ? 0.6 : 1 }}
            onMouseEnter={(e) => { if (!isLoading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '6px 6px 16px rgba(16,185,129,0.35)'; } }}
            onMouseLeave={(e) => { if (!isLoading) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '4px 4px 12px rgba(16,185,129,0.25)'; } }}
          >
            {isLoading ? 'Syncing...' : 'Sync All Models'}
          </button>
          
          {/* Reload Button */}
          <button
            onClick={loadAllModels}
            disabled={isLoading}
            style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: isLoading ? 'not-allowed' : 'pointer', boxShadow: '4px 4px 12px rgba(99,102,241,0.25)', transition: 'all 0.2s ease', opacity: isLoading ? 0.6 : 1 }}
            onMouseEnter={(e) => { if (!isLoading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '6px 6px 16px rgba(99,102,241,0.35)'; } }}
            onMouseLeave={(e) => { if (!isLoading) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '4px 4px 12px rgba(99,102,241,0.25)'; } }}
          >
            {isLoading ? 'Loading...' : 'Reload'}
          </button>
        </div>

        <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
          Showing {filteredModels.length} of {allModels.length} models
        </div>
      </div>

      {/* Models Table */}
      <div style={{ background: nm.bg, borderRadius: '16px', boxShadow: nm.shadowSm, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
                <th style={{ padding: '16px', textAlign: 'left', color: '#fff', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', borderRight: '1px solid rgba(255,255,255,0.1)' }}>SR</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#fff', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', borderRight: '1px solid rgba(255,255,255,0.1)' }}>Function</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#fff', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', borderRight: '1px solid rgba(255,255,255,0.1)' }}>Provider</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#fff', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', borderRight: '1px solid rgba(255,255,255,0.1)' }}>Model Name</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#fff', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', borderRight: '1px solid rgba(255,255,255,0.1)' }}>Context</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#fff', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', borderRight: '1px solid rgba(255,255,255,0.1)' }}>Input/M</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#fff', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', borderRight: '1px solid rgba(255,255,255,0.1)' }}>Output/M</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#fff', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', borderRight: '1px solid rgba(255,255,255,0.1)' }}>Total</th>
                <th style={{ padding: '16px', textAlign: 'center', color: '#fff', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', borderRight: '1px solid rgba(255,255,255,0.1)' }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'center', color: '#fff', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>Default</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="10" style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
                    Loading models...
                  </td>
                </tr>
              ) : filteredModels.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
                    No models found
                  </td>
                </tr>
              ) : (
                filteredModels.map((model, index) => (
                  <tr key={model.id} style={{ background: index % 2 === 0 ? nm.bg : '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#64748b', fontWeight: '600', borderRight: '1px solid #e2e8f0' }}>{model.srNo}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0f172a', fontWeight: '600', borderRight: '1px solid #e2e8f0' }}>
                      <span style={{ padding: '4px 10px', borderRadius: '6px', background: model.function === 'Chat' ? '#dbeafe' : model.function === 'Image' ? '#e0e7ff' : model.function === 'Video' ? '#f3e8ff' : '#d1fae5', color: model.function === 'Chat' ? '#1e40af' : model.function === 'Image' ? '#4338ca' : model.function === 'Video' ? '#6b21a8' : '#065f46', fontSize: '11px', fontWeight: '700' }}>
                        {model.function}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0f172a', fontWeight: '600', borderRight: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {providerLogos[model.provider] && (
                          <img src={providerLogos[model.provider]} alt={model.provider} style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                        )}
                        {model.provider}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0f172a', fontWeight: '500', borderRight: '1px solid #e2e8f0' }}>{model.modelName}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#64748b', fontWeight: '500', borderRight: '1px solid #e2e8f0' }}>{model.contextWindow}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#64748b', fontWeight: '500', borderRight: '1px solid #e2e8f0' }}>{model.inputPerMillion}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#64748b', fontWeight: '500', borderRight: '1px solid #e2e8f0' }}>{model.outputPerMillion}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0f172a', fontWeight: '600', borderRight: '1px solid #e2e8f0' }}>{model.total}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderRight: '1px solid #e2e8f0' }}>
                      <button
                        onClick={() => handleToggleStatus(model)}
                        style={{
                          padding: '6px 16px',
                          borderRadius: '8px',
                          border: 'none',
                          background: model.status === 'active' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                          color: '#fff',
                          fontSize: '11px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          boxShadow: model.status === 'active' ? '2px 2px 8px rgba(16, 185, 129, 0.3)' : '2px 2px 8px rgba(239, 68, 68, 0.3)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                      >
                        {model.status === 'active' ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleToggleDefault(model)}
                        disabled={model.status !== 'active'}
                        title={model.status !== 'active' ? 'Activate model first to set as default' : 'Set as default for landing page'}
                        style={{
                          padding: '8px',
                          borderRadius: '8px',
                          border: 'none',
                          background: model.isDefault ? 'linear-gradient(135deg, #f59e0b, #d97706)' : nm.bg,
                          color: model.isDefault ? '#fff' : '#94a3b8',
                          fontSize: '16px',
                          cursor: model.status === 'active' ? 'pointer' : 'not-allowed',
                          boxShadow: model.isDefault ? '2px 2px 8px rgba(245, 158, 11, 0.3)' : nm.shadowSm,
                          transition: 'all 0.2s ease',
                          opacity: model.status !== 'active' ? 0.4 : 1
                        }}
                        onMouseEnter={(e) => { if (model.status === 'active') e.currentTarget.style.transform = 'scale(1.1)'; }}
                        onMouseLeave={(e) => { if (model.status === 'active') e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        ⭐
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
